import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOptgroup, MatOption, MatOptionSelectionChange } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate,
} from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatSelectChange, MatSelectTrigger } from '@angular/material/select';
import { SliceCurrencyDirective } from '@shared/directives/currency.directive';
import { SliceDatepickerDateStringDirective } from '@shared/directives/datepicker-date-string.directive';
import { SliceInputPasteToStringListDirective } from '@shared/directives/input-paste-to-string-list.directive';
import { SliceOptionSelectAllDirective } from '@shared/directives/option-select-all.directive';
import { SlicePopoverDirective } from '@shared/directives/popover.directive';
import { SliceSelectDirective } from '@shared/directives/select.directive';
import { SliceTouchSelectAllDirective } from '@shared/directives/touch-select-all.directive';
import { Condition } from '@ucast/mongo2js';
import { uniqBy } from 'lodash-es';
import { DataManagerController } from '../application/data-manager-controller';
import { DataManagerFiltro } from '../state/data-manager-filtro';

export type Filtro = {
  (object: any): boolean;
  ast: Condition;
};

@Component({
  selector: 'app-data-manager-filtro',
  imports: [
    DatePipe,
    DecimalPipe,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDateRangeInput,
    MatDateRangePicker,
    MatEndDate,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatSelectTrigger,
    MatStartDate,
    MatSuffix,
    SliceCurrencyDirective,
    ReactiveFormsModule,
    SliceInputPasteToStringListDirective,
    SliceOptionSelectAllDirective,
    SlicePopoverDirective,
    SliceSelectDirective,
    SliceDatepickerDateStringDirective,
    SliceTouchSelectAllDirective,
    MatOptgroup,
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './data-manager-filtro.component.html',
  styleUrls: ['./data-manager-filtro.component.scss'],
})
export class DataManagerFiltroComponent<T> {
  // Inputs
  dmController = input.required<DataManagerController<T>>();
  info = input<any>();
  required = signal(false);

  // Injectables
  private cdr = inject(ChangeDetectorRef);

  // Protected
  protected filtrando = computed(() => this.dmController().filtrando());
  protected filtros = this.buildFiltros();
  protected form = new FormGroup({});
  protected totalLinhas = computed(() => this.dmController().dados().length);

  // Effect
  private filtrosPronto = effect(() => {
    const filtros = this.dmController().filtros;
    if (filtros.filtrosPronto()) {
      this.cdr.markForCheck();
    }
  });

  onAplicar(event: Event) {
    this.required.set(true);
    event.preventDefault();
    this.dmController().refresh();
  }

  onLimparTodosFiltros() {
    this.filtros().limpar();
  }

  onCancelar() {
    this.dmController().filtroCancelar();
  }

  protected optionsGrupoUniq(filtro: DataManagerFiltro) {
    return uniqBy(filtro.options(), 'grupo').length > 1;
  }

  private marcandoTodos = false;
  onMarcarTodos(change: MatSelectChange | MatOptionSelectionChange) {
    if (change instanceof MatOptionSelectionChange) {
      if (!change.isUserInput || change.source.value !== undefined) {
        return;
      }
      this.marcandoTodos = true;
      return;
    }

    const select = change.source as MatSelect;
    const optionsLen = select.options.length;
    const selected = select.selected as Array<any>;
    if (this.marcandoTodos) {
      this.marcandoTodos = false;
      if (optionsLen - 1 > selected.length) {
        select.options.forEach((opt) => opt.select(true));
      } else {
        select.options.forEach((opt) => opt.deselect(true));
      }
    } else {
      const marcarTodosOption = select.options.get(0);
    }
  }

  onSelectTriggerInputChange(event: Event, filtro: DataManagerFiltro) {
    const element = event.currentTarget as HTMLInputElement;
    const valor = element.value.trim();
    const valorControl = filtro.valorAsControl;
    if (valor.length === 0) {
      valorControl.reset();
      return;
    }
    valorControl.setValue(valor.split(','));
    valorControl.markAsDirty();
  }

  onLimparFiltro(filtro: DataManagerFiltro) {
    filtro.reset();
  }

  private buildFiltros() {
    return computed(() => {
      const filtros = this.dmController().filtros;
      for (const filtro of filtros) {
        const uniqueGroups = uniqBy(filtro.options(), 'grupo').filter((o) => o.grupo);
        Object.assign(filtro, { numeroGrupos: uniqueGroups.length });
      }
      return filtros;
    });
  }

  @HostListener('document:keyup', ['$event'])
  protected onKeyup(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    // O MatSelect trata estes eventos
    if (target.tagName === 'MAT-SELECT') {
      return;
    }

    if (event.key === 'Escape') {
      return this.dmController().filtroCancelar();
    }

    if (event.key === 'Enter' && !this.filtros().invalid) {
      return this.dmController().refresh();
    }
  }
}
